/**
 * Public share view — renders a single note via a share token. Read-only;
 * the previous read/write mode was removed.
 */

import { useEffect, useMemo, useRef } from "react";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { renderMarkdown } from "../lib/markdown";
import { renderMermaidIn } from "../lib/mermaid";
import type { Note } from "../api/types";

export const Route = createFileRoute("/s/$token")({
  component: PublicShareView,
});

interface ShareResponse {
  note: Note;
  permission: "read";
  expires_at: string | null;
}

function PublicShareView() {
  const { token } = useParams({ from: "/s/$token" });
  const previewRef = useRef<HTMLDivElement | null>(null);

  const shareQ = useQuery({
    queryKey: ["public-share", token],
    queryFn: () => api.get<ShareResponse>(`/public/shares/${token}`),
    retry: false,
  });

  const note = shareQ.data?.note;
  const html = useMemo(() => renderMarkdown(note?.body || ""), [note?.body]);

  useEffect(() => {
    if (previewRef.current) void renderMermaidIn(previewRef.current);
  }, [html]);

  if (shareQ.isPending) {
    return (
      <div className="p-8 text-center text-sm" style={{ color: "var(--fg-muted)" }}>
        Loading shared note…
      </div>
    );
  }
  if (shareQ.error || !note) {
    return (
      <div className="p-8 text-center">
        <h1 className="text-base font-semibold mb-2">Link unavailable</h1>
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          This share has been revoked or expired.
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div
        className="rounded-lg border p-4"
        style={{ borderColor: "var(--border)", background: "var(--bg-elevated)" }}
      >
        <div className="mb-2 text-xs uppercase tracking-wide" style={{ color: "var(--fg-muted)" }}>
          Shared note · Read-only
        </div>
        {note.title && <h1 className="text-lg font-semibold mb-2">{note.title}</h1>}
        <div ref={previewRef} className="note-md-body" dangerouslySetInnerHTML={{ __html: html }} />
        {note.attachments && note.attachments.length > 0 && (
          <div className="mt-3 grid grid-cols-2 gap-2">
            {note.attachments.map((a) => (
              <img key={a.id} src={a.url} alt={a.filename} className="rounded" />
            ))}
          </div>
        )}
      </div>
      <p className="text-xs mt-4 text-center" style={{ color: "var(--fg-muted)" }}>
        Powered by <strong>keep</strong>.
      </p>
    </div>
  );
}
